import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  InputAdornment,
  IconButton,
  Drawer,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';

// API base - set `REACT_APP_API_BASE` in .env if needed
const API_BASE = process.env.REACT_APP_API_BASE;

export default function NomineeAdd({ userid, user }) {
  const isAdmin = user?.user_role === 'ADMIN';
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState({
    id: null,
    name: '',
    gender: 'Male',
    mobile: '',
    aadhar: '',
    vidhansabha: 'Karad South',
    karad: { village: '', ganName: '', zpName: '', reference: '' },
    other: { village: '', taluka: '', district: '' },
    formDate: '',
    registrationNo: '',
    formOwner: '',
    whatsappMobile: '',
    cashAmount: '',
    onlineAmount: '',
    paymentDate: '',
    remarks: '',
  });
  const [editing, setEditing] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    name: '',
    mobile: '',
    aadhar: '',
    village: '',
    registrationNo: '',
    formOwner: '',
    formDateFrom: '',
    formDateTo: '',
  });

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/nominee-records`, {
        headers: { 'Accept': 'application/json' }
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`HTTP ${res.status}: ${text}`);
      }
      const data = await res.json();
      setRecords(data.map(mapFromApi));
    } catch (err) {
      console.error('Fetch error:', err.message);
    } finally {
      setLoading(false);
    }
  };

  function mapFromApi(r) {
    const isKarad = r.vidhansabha === 'Karad South';
    return {
      id: r.id,
      name: r.name || '',
      gender: r.gender || 'Male',
      mobile: r.mobile || '',
      aadhar: r.aadhar || '',
      vidhansabha: r.vidhansabha || 'Karad South',
      karad: {
        village: isKarad ? (r.village || '') : '',
        ganName: r.gan_name || '',
        zpName: r.zp_name || '',
        reference: r.reference || '',
      },
      other: {
        village: !isKarad ? (r.village || '') : '',
        taluka: r.taluka || '',
        district: r.district || '',
      },
      formDate: r.form_date || '',
      registrationNo: r.registration_no || '',
      formOwner: r.form_owner || '',
      whatsappMobile: r.whatsapp_mobile || '',
      cashAmount: r.cash_amount || '',
      onlineAmount: r.online_amount || '',
      paymentDate: r.payment_date || '',
      remarks: r.remarks || '',
    };
  }

  function mapToApi(f, uid) {
    const isKarad = f.vidhansabha === 'Karad South';
    return {
      userid: uid,
      name: f.name,
      gender: f.gender,
      mobile: f.mobile,
      aadhar: f.aadhar,
      vidhansabha: f.vidhansabha,
      village: isKarad ? f.karad.village : f.other.village,
      gan_name: f.karad.ganName,
      zp_name: f.karad.zpName,
      reference: f.karad.reference,
      taluka: f.other.taluka,
      district: f.other.district,
      form_date: f.formDate || null,
      registration_no: f.registrationNo,
      form_owner: f.formOwner,
      whatsapp_mobile: f.whatsappMobile,
      cash_amount: f.cashAmount || null,
      online_amount: f.onlineAmount || null,
      payment_date: f.paymentDate || null,
      remarks: f.remarks,
    };
  }

  function handleChange(e) {
    const { name, value } = e.target;
    if (name.startsWith('karad.')) {
      const k = name.split('.')[1];
      setForm((s) => ({ ...s, karad: { ...s.karad, [k]: value } }));
      return;
    }
    if (name.startsWith('other.')) {
      const k = name.split('.')[1];
      setForm((s) => ({ ...s, other: { ...s.other, [k]: value } }));
      return;
    }
    setForm((s) => ({ ...s, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const method = form.id ? 'PUT' : 'POST';
    const url = form.id ? `${API_BASE}/nominee-records/${form.id}` : `${API_BASE}/nominee-records`;

    try {
      const payload = mapToApi(form, userid);
      if (form.id) payload._user_role = user?.user_role || '';
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        fetchRecords();
        resetForm();
        setDrawerOpen(false);
      } else {
        const errData = await res.json();
        console.error('Save error Details:', errData);
        alert(`Failed to save record: ${errData.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Save error:', err);
    }
  }

  function handleEdit(id) {
    const rec = records.find((r) => r.id === id);
    if (!rec) return;
    if (!isAdmin && rec.is_locked && !rec.unlock_granted_by) {
      alert('This record is locked. Please ask an admin to unlock it first.');
      return;
    }
    setForm({ ...rec });
    setEditing(true);
    setDrawerOpen(true);
  }

  async function handleToggleLock(id) {
    try {
      const res = await fetch(`${API_BASE}/nominee-records/${id}/toggle-lock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ admin_name: user?.namename || 'admin' })
      });
      if (!res.ok) throw new Error('Toggle lock failed');
      await fetchRecords();
    } catch (err) {
      alert('Error toggling lock: ' + err.message);
    }
  }

  async function handleDelete(id) {
    const rec = records.find((r) => r.id === id);
    if (!rec) return;
    if (!isAdmin && rec.is_locked && !rec.unlock_granted_by) {
      alert('This record is locked. Please ask an admin to unlock it first.');
      return;
    }

    if (!window.confirm('Delete this record?')) return;
    try {
      const res = await fetch(`${API_BASE}/nominee-records/${id}`, { method: 'DELETE' });
      if (res.ok) {
        fetchRecords();
      } else {
        alert('Failed to delete');
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  }

  function handleFilterChange(e) {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  }

  const filteredRecords = records.filter((r) => {
    const m = (val = '') => (val == null ? '' : String(val).toLowerCase());
    // Date range filter for formDate
    let formDateValid = true;
    if (filters.formDateFrom) {
      formDateValid = formDateValid && r.formDate >= filters.formDateFrom;
    }
    if (filters.formDateTo) {
      formDateValid = formDateValid && r.formDate <= filters.formDateTo;
    }
    return (
      m(r.name).includes(m(filters.name)) &&
      m(r.mobile).includes(m(filters.mobile)) &&
      m(r.aadhar).includes(m(filters.aadhar)) &&
      (m(r.karad?.village).includes(m(filters.village)) || m(r.other?.village).includes(m(filters.village))) &&
      m(r.registrationNo).includes(m(filters.registrationNo)) &&
      m(r.formOwner).includes(m(filters.formOwner)) &&
      formDateValid
    );
  });

  function resetForm() {
    setForm({
      id: null,
      name: '',
      gender: 'Male',
      mobile: '',
      aadhar: '',
      vidhansabha: 'Karad South',
      karad: { village: '', ganName: '', zpName: '', reference: '' },
      other: { village: '', taluka: '', district: '' },
      formDate: '',
      registrationNo: '',
      formOwner: '',
      whatsappMobile: '',
      cashAmount: '',
      onlineAmount: '',
      paymentDate: '',
      remarks: '',
    });
    setEditing(false);
    setDrawerOpen(false);
  }

  return (
    <Box sx={{ p: 1 }}>
      <Paper sx={{ p: 2, bgcolor: 'white' }} elevation={4}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <Typography variant="h6" sx={{ color: '#0b4a6f', flexGrow: 1, fontWeight: 'bold' }}>Nominee Records</Typography>
          <Chip label={`${records.length} records`} color="primary" variant="outlined" size="small" />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => { resetForm(); setDrawerOpen(true); }}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
          >
            Add New Nominee
          </Button>
        </Box>

        <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)', border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {['Name', 'Mobile', 'Aadhar', 'Village', 'Form Date', 'Reg. No', 'Owner', 'Remarks', 'Actions'].map((h) => (
                  <TableCell key={h} sx={{ bgcolor: '#0b4a6f', color: 'white', fontWeight: 'bold', py: 1.5 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="name" value={filters.name} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="primary" /></InputAdornment>, disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="mobile" value={filters.mobile} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="aadhar" value={filters.aadhar} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="village" value={filters.village} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1, minWidth: 150 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <TextField
                      size="small"
                      variant="standard"
                      name="formDateFrom"
                      type="date"
                      value={filters.formDateFrom}
                      onChange={handleFilterChange}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ disableUnderline: true }}
                      sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black', width: '100%' }}
                      placeholder="From"
                    />
                    <TextField
                      size="small"
                      variant="standard"
                      name="formDateTo"
                      type="date"
                      value={filters.formDateTo}
                      onChange={handleFilterChange}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ disableUnderline: true }}
                      sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black', width: '100%' }}
                      placeholder="To"
                    />
                  </Box>
                </TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="registrationNo" value={filters.registrationNo} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="formOwner" value={filters.formOwner} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }} />
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRecords.map((r) => (
                <TableRow key={r.id} hover sx={{ '&:nth-of-type(even)': { bgcolor: '#fafafa' } }}>
                  <TableCell sx={{ fontWeight: 500 }}>{r.name}</TableCell>
                  <TableCell>{r.mobile}</TableCell>
                  <TableCell>{r.aadhar}</TableCell>
                  <TableCell>{r.karad?.village || r.other?.village}</TableCell>
                  <TableCell>{formatDateDMY(r.formDate)}</TableCell>
                  <TableCell>{r.registrationNo}</TableCell>
                  <TableCell>{r.formOwner}</TableCell>
                  <TableCell>{r.remarks}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      {isAdmin && (
                        <IconButton size="small" onClick={() => handleToggleLock(r.id)}
                          title={r.unlock_granted_by ? `Unlock granted — click to re-lock` : 'Click to unlock for editing'}
                          sx={{ bgcolor: r.unlock_granted_by ? '#fff3e0' : '#fce4ec', '&:hover': { bgcolor: r.unlock_granted_by ? '#ffe0b2' : '#f8bbd0' } }}>
                          {r.unlock_granted_by ? <LockOpenIcon fontSize="small" color="warning" /> : <LockIcon fontSize="small" color="error" />}
                        </IconButton>
                      )}
                      <IconButton size="small" color="primary" onClick={() => handleEdit(r.id)}
                        disabled={!isAdmin && r.is_locked && !r.unlock_granted_by}
                        title={!isAdmin && r.is_locked && !r.unlock_granted_by ? 'Record is locked' : 'Edit'}
                        sx={{ bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' }, opacity: (!isAdmin && r.is_locked && !r.unlock_granted_by) ? 0.4 : 1 }}>
                        {(!isAdmin && r.is_locked && !r.unlock_granted_by) ? <LockIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(r.id)} 
                        disabled={!isAdmin && r.is_locked && !r.unlock_granted_by}
                        title={!isAdmin && r.is_locked && !r.unlock_granted_by ? 'Record is locked' : 'Delete'}
                        sx={{ bgcolor: '#ffebee', '&:hover': { bgcolor: '#ffcdd2' }, opacity: (!isAdmin && r.is_locked && !r.unlock_granted_by) ? 0.4 : 1 }}>
                        {(!isAdmin && r.is_locked && !r.unlock_granted_by) ? <LockIcon fontSize="small" /> : <DeleteIcon fontSize="small" />}
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box component="form" onSubmit={handleSubmit} sx={{ width: { xs: '100vw', sm: 500 }, p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: '#0b4a6f', flexGrow: 1, fontWeight: 'bold' }}>
              {editing ? 'Edit Nominee' : 'Add New Nominee'}
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', flexGrow: 1, pr: 1 }}>
            <TextField label="Worker's Name" name="name" value={form.name} onChange={handleChange} required fullWidth variant="outlined" />

            <FormControl fullWidth>
              <InputLabel id="gender-label">Gender</InputLabel>
              <Select labelId="gender-label" label="Gender" name="gender" value={form.gender} onChange={handleChange}>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </Select>
            </FormControl>

            <TextField label="Mobile No" name="mobile" value={form.mobile} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="WhatsApp Mobile No" name="whatsappMobile" value={form.whatsappMobile} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="Aadhar No" name="aadhar" value={form.aadhar} onChange={handleChange} fullWidth variant="outlined" />

            {/* <FormControl fullWidth>
              <InputLabel id="vidh-label">Vidhansabha</InputLabel>
              <Select labelId="vidh-label" label="Vidhansabha" name="vidhansabha" value={form.vidhansabha} onChange={handleChange}>
                <MenuItem value="Karad South">Karad South</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl> */}

            <TextField label="Village" name="other.village" value={form.other.village} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="Taluka" name="other.taluka" value={form.other.taluka} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="District" name="other.district" value={form.other.district} onChange={handleChange} fullWidth variant="outlined" />

            <TextField label="Form Date" name="formDate" type="date" InputLabelProps={{ shrink: true }} value={form.formDate} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="Registration No" name="registrationNo" value={form.registrationNo} onChange={handleChange} fullWidth variant="outlined" />
            <FormControl fullWidth variant="outlined">
              <InputLabel id="form-owner-label">Form Owner</InputLabel>
              <Select labelId="form-owner-label" label="Form Owner" name="formOwner" value={form.formOwner} onChange={handleChange}>
                <MenuItem value="NITIN SIR">NITIN SIR</MenuItem>
                <MenuItem value="KUMBHAR MADAM">KUMBHAR MADAM</MenuItem>
                <MenuItem value="KAMBALE MADAM">KAMBALE MADAM</MenuItem>
                <MenuItem value="TAMBAVE OFFICE">TAMBAVE OFFICE</MenuItem>
              </Select>
            </FormControl>

            <Stack direction="row" spacing={2}>
              <TextField
                label="Cash Amount Rs."
                name="cashAmount"
                type="number"
                value={form.cashAmount}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />
              <TextField
                label="Online Amount Rs."
                name="onlineAmount"
                type="number"
                value={form.onlineAmount}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />
            </Stack>
            <TextField label="Payment Date" name="paymentDate" type="date" InputLabelProps={{ shrink: true }} value={form.paymentDate} onChange={handleChange} fullWidth variant="outlined" />

            <TextField label="Remarks" name="remarks" value={form.remarks} onChange={handleChange} multiline rows={3} fullWidth variant="outlined" />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={2}>
            <Button type="submit" variant="contained" color="primary" fullWidth size="large" sx={{ borderRadius: 2 }}>
              {editing ? 'Update' : 'Save'}
            </Button>
            <Button variant="outlined" onClick={resetForm} fullWidth size="large" sx={{ borderRadius: 2 }}>
              Reset
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </Box>
  );
}

// Helper to format date as dd/mm/yyyy
function formatDateDMY(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString('en-GB');
}
